import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { MapPin, Monitor, Tag, Layers, ChevronDown, Check, Search } from 'lucide-react';
import type { SimulateNewRuleRequest, SegmentationOptions } from '@/types/price-rules';

interface Props {
    formData: Partial<SimulateNewRuleRequest>;
    setFormData: (data: Partial<SimulateNewRuleRequest>) => void;
    segmentationOptions: SegmentationOptions;
}

const UTM_OPTIONS = [
    { value: 'utm_campaign', label: 'UTM Campaign', description: 'Nome da campanha' },
    { value: 'utm_source', label: 'UTM Source', description: 'Origem do tráfego' },
    { value: 'utm_content', label: 'UTM Content', description: 'Variação do conteúdo' },
    { value: 'utm_medium', label: 'UTM Medium', description: 'Meio de marketing' },
    { value: 'utm_term', label: 'UTM Term', description: 'Termo de busca pago' },
    { value: 'id_post', label: 'ID Post', description: 'Identificador do post' },
];

export default function StepSegmentation({ formData, setFormData, segmentationOptions }: Props) {
    const [countrySearch, setCountrySearch] = useState('');
    const [countryOpen, setCountryOpen] = useState(false);
    const [adUnitOpen, setAdUnitOpen] = useState(false);
    const [deviceOpen, setDeviceOpen] = useState(false);
    const [utmOpen, setUtmOpen] = useState(false);

    const { countries, ad_units, devices } = segmentationOptions;

    const filteredCountries = countries.filter(c =>
        c.nome.toLowerCase().includes(countrySearch.toLowerCase()) ||
        c.codigo.toLowerCase().includes(countrySearch.toLowerCase())
    );

    const getCountryLabel = (codigo: string) => {
        if (codigo === '*') return 'Todos os países';
        const c = countries.find(c => c.codigo === codigo);
        return c ? `${c.nome} (${c.codigo})` : codigo;
    };

    const getDeviceLabel = (device: string) => {
        const d = devices.find(d => d.value === device);
        return d?.label || device;
    };

    const getAdUnitLabel = (adUnit: string) => {
        if (adUnit === '*') return 'Todos os blocos';
        const a = ad_units.find(a => a.value === adUnit);
        return a?.label || adUnit;
    };

    const getUtmLabel = (key: string) => {
        if (!key) return 'Selecione uma chave';
        const utm = UTM_OPTIONS.find(u => u.value === key);
        return utm?.label || key;
    };

    const closeAllDropdowns = () => {
        setCountryOpen(false);
        setAdUnitOpen(false);
        setDeviceOpen(false);
        setUtmOpen(false);
        setCountrySearch('');
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-lg font-semibold text-gray-900">Configure a Segmentação</h2>
                <p className="text-sm text-gray-500 mt-1">Os dados serão consultados no GAM com esses filtros.</p>
            </div>

            {/* País, Ad Unit, Device - 3 colunas */}
            <div className="grid gap-4 md:grid-cols-3">
                {/* País */}
                <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <div className="p-1.5 bg-green-100 rounded-md">
                            <MapPin className="h-4 w-4 text-green-600" />
                        </div>
                        País
                    </Label>
                    <div className="relative">
                        <button
                            type="button"
                            onClick={() => { closeAllDropdowns(); setCountryOpen(!countryOpen); }}
                            className="w-full h-11 px-4 text-left text-sm font-medium border-2 border-gray-200 rounded-xl bg-white flex items-center justify-between hover:border-gray-300 focus:outline-none focus:border-green-500"
                        >
                            <span className={formData.country === '*' ? 'text-gray-500' : ''}>
                                {getCountryLabel(formData.country || '*')}
                            </span>
                            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${countryOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {countryOpen && (
                            <>
                                <div className="fixed inset-0 z-10" onClick={closeAllDropdowns} />
                                <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                    <div className="p-2 border-b border-gray-100">
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                            <input
                                                type="text"
                                                placeholder="Buscar..."
                                                value={countrySearch}
                                                onChange={(e) => setCountrySearch(e.target.value)}
                                                className="w-full h-9 pl-9 pr-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-green-500"
                                                onClick={(e) => e.stopPropagation()}
                                                autoFocus
                                            />
                                        </div>
                                    </div>
                                    <div className="max-h-[200px] overflow-y-auto">
                                        <button
                                            type="button"
                                            onClick={() => { setFormData({ ...formData, country: '*' }); closeAllDropdowns(); }}
                                            className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                                                formData.country === '*' ? 'bg-green-50 text-green-700 font-medium' : ''
                                            }`}
                                        >
                                            🌍 Todos os países
                                            {formData.country === '*' && <Check className="h-4 w-4" />}
                                        </button>
                                        {filteredCountries.map((c) => (
                                            <button
                                                key={c.codigo}
                                                type="button"
                                                onClick={() => { setFormData({ ...formData, country: c.codigo }); closeAllDropdowns(); }}
                                                className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                                                    formData.country === c.codigo ? 'bg-green-50 text-green-700 font-medium' : ''
                                                }`}
                                            >
                                                {c.nome} ({c.codigo})
                                                {formData.country === c.codigo && <Check className="h-4 w-4" />}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Ad Unit */}
                <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <div className="p-1.5 bg-blue-100 rounded-md">
                            <Layers className="h-4 w-4 text-blue-600" />
                        </div>
                        Ad Unit
                    </Label>
                    <div className="relative">
                        <button
                            type="button"
                            onClick={() => { closeAllDropdowns(); setAdUnitOpen(!adUnitOpen); }}
                            className="w-full h-11 px-4 text-left text-sm font-medium border-2 border-gray-200 rounded-xl bg-white flex items-center justify-between hover:border-gray-300 focus:outline-none focus:border-blue-500"
                        >
                            <span className={formData.ad_unit === '*' ? 'text-gray-500' : ''}>
                                {getAdUnitLabel(formData.ad_unit || '*')}
                            </span>
                            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${adUnitOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {adUnitOpen && (
                            <>
                                <div className="fixed inset-0 z-10" onClick={closeAllDropdowns} />
                                <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                    <div className="max-h-[200px] overflow-y-auto">
                                        <button
                                            type="button"
                                            onClick={() => { setFormData({ ...formData, ad_unit: '*' }); closeAllDropdowns(); }}
                                            className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                                                formData.ad_unit === '*' ? 'bg-blue-50 text-blue-700 font-medium' : ''
                                            }`}
                                        >
                                            Todos os blocos
                                            {formData.ad_unit === '*' && <Check className="h-4 w-4" />}
                                        </button>
                                        {ad_units.map((a) => (
                                            <button
                                                key={a.value}
                                                type="button"
                                                onClick={() => { setFormData({ ...formData, ad_unit: a.value }); closeAllDropdowns(); }}
                                                className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                                                    formData.ad_unit === a.value ? 'bg-blue-50 text-blue-700 font-medium' : ''
                                                }`}
                                            >
                                                {a.label}
                                                {formData.ad_unit === a.value && <Check className="h-4 w-4" />}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Device */}
                <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <div className="p-1.5 bg-purple-100 rounded-md">
                            <Monitor className="h-4 w-4 text-purple-600" />
                        </div>
                        Device
                    </Label>
                    <div className="relative">
                        <button
                            type="button"
                            onClick={() => { closeAllDropdowns(); setDeviceOpen(!deviceOpen); }}
                            className="w-full h-11 px-4 text-left text-sm font-medium border-2 border-gray-200 rounded-xl bg-white flex items-center justify-between hover:border-gray-300 focus:outline-none focus:border-purple-500"
                        >
                            <span className={formData.device === '*' ? 'text-gray-500' : ''}>
                                {getDeviceLabel(formData.device || '*')}
                            </span>
                            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${deviceOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {deviceOpen && (
                            <>
                                <div className="fixed inset-0 z-10" onClick={closeAllDropdowns} />
                                <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                    <div className="max-h-[200px] overflow-y-auto">
                                        {devices.map((d) => (
                                            <button
                                                key={d.value}
                                                type="button"
                                                onClick={() => { setFormData({ ...formData, device: d.value }); closeAllDropdowns(); }}
                                                className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                                                    formData.device === d.value ? 'bg-purple-50 text-purple-700 font-medium' : ''
                                                }`}
                                            >
                                                <span className="flex items-center gap-2">
                                                    {d.value === 'MOBILE' && '📱'}
                                                    {d.value === 'WEB' && '💻'}
                                                    {d.value === '*' && '🌐'}
                                                    {d.label}
                                                </span>
                                                {formData.device === d.value && <Check className="h-4 w-4" />}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Segmentação Customizada */}
            <div className="space-y-3">
                <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <div className="p-1.5 bg-orange-100 rounded-md">
                        <Tag className="h-4 w-4 text-orange-600" />
                    </div>
                    Segmentação customizada
                    <span className="text-xs text-red-500">*</span>
                </Label>

                <div className="grid gap-4 md:grid-cols-2">
                    {/* UTM Key Select */}
                    <div className="space-y-1.5">
                        <label className="text-xs text-gray-500">Chave <span className="text-red-500">*</span></label>
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => { closeAllDropdowns(); setUtmOpen(!utmOpen); }}
                                className="w-full h-11 px-4 text-left text-sm font-medium border-2 border-gray-200 rounded-xl bg-white flex items-center justify-between hover:border-gray-300 focus:outline-none focus:border-orange-500"
                            >
                                <span className={!formData.custom_key ? 'text-gray-500' : ''}>
                                    {getUtmLabel(formData.custom_key || '')}
                                </span>
                                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${utmOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {utmOpen && (
                                <>
                                    <div className="fixed inset-0 z-10" onClick={closeAllDropdowns} />
                                    <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                        <div className="max-h-[240px] overflow-y-auto">
                                            {UTM_OPTIONS.map((utm) => (
                                                <button
                                                    key={utm.value}
                                                    type="button"
                                                    onClick={() => { setFormData({ ...formData, custom_key: utm.value }); closeAllDropdowns(); }}
                                                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between ${
                                                        formData.custom_key === utm.value ? 'bg-orange-50' : ''
                                                    }`}
                                                >
                                                    <div>
                                                        <div className={`text-sm ${formData.custom_key === utm.value ? 'text-orange-700 font-medium' : ''}`}>
                                                            {utm.label}
                                                        </div>
                                                        <div className="text-xs text-gray-500">{utm.description}</div>
                                                    </div>
                                                    {formData.custom_key === utm.value && <Check className="h-4 w-4 text-orange-600" />}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Valor Input */}
                    <div className="space-y-1.5">
                        <label className="text-xs text-gray-500">Valor <span className="text-red-500">*</span></label>
                        <Input
                            placeholder="ex: google, facebook, push-app"
                            value={formData.custom_value || ''}
                            onChange={(e) => setFormData({ ...formData, custom_value: e.target.value || null })}
                            className="h-11 bg-white border-2 border-gray-200 rounded-xl focus:border-orange-500"
                        />
                    </div>
                </div>
            </div>

            {/* Summary */}
            <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Resumo da consulta</h4>
                <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-gray-700 text-sm rounded-lg border border-gray-200 shadow-sm">
                        <MapPin className="h-3.5 w-3.5 text-green-600" />
                        {getCountryLabel(formData.country || '*')}
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-gray-700 text-sm rounded-lg border border-gray-200 shadow-sm">
                        <Layers className="h-3.5 w-3.5 text-blue-600" />
                        {getAdUnitLabel(formData.ad_unit || '*')}
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-gray-700 text-sm rounded-lg border border-gray-200 shadow-sm">
                        <Monitor className="h-3.5 w-3.5 text-purple-600" />
                        {getDeviceLabel(formData.device || '*')}
                    </span>
                    {formData.custom_key && formData.custom_value && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-700 text-sm rounded-lg border border-orange-200 shadow-sm">
                            <Tag className="h-3.5 w-3.5" />
                            {formData.custom_key}={formData.custom_value}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}